import { Story } from '../../types/story';

export const edinburghBookshop: Story = {
  id: 'edinburgh-bookshop',
  title: 'The Last Page',
  excerpt: 'In a dusty Edinburgh bookshop, a rare book dealer and a literature student discover that some stories are meant to be lived, not just read, especially when the book is about forbidden Victorian passions.',
  city: 'Edinburgh',
  publishDate: '2026-01-29',
  readTime: '32 min read',
  tags: ['♂♀', 'bookshop', 'academic', 'forbidden', 'intense'],
  categories: ['intense', 'forbidden'],
  intensity: 3,
  content: `The bookshop smelled like old paper and rain and secrets. That's what had drawn me to it in the first place—The Last Page, tucked away in a close off the Royal Mile, the kind of place tourists never found but locals cherished.

I was a literature student at the university, supposed to be writing my dissertation on Victorian erotic literature, but mostly I was just avoiding my advisor and collecting rare books instead. The owner, Alistair, was a man in his late forties with silver hair at his temples and eyes that had seen too many stories.

"Another one for your collection?" he asked, not looking up from the book he was cataloging.

"Something different today," I said, leaning against the counter. "Something... authentic."

He finally looked at me, his eyes crinkling at the corners. "Authentic is expensive, my dear. And dangerous."

"I can handle dangerous."

That's when she walked in. The bell above the door chimed, and there she was—maybe twenty-five, with dark hair pulled back in a loose bun, wearing a coat that had seen better days but carrying herself like she owned the world.

"Professor MacLeod?" she asked, her voice soft but clear.

Alistair straightened up immediately. "Dr. Campbell. I wasn't expecting you."

"Research," she said simply, her eyes scanning the shop before landing on me. "And perhaps... something else."

"This is Emma," Alistair said to me. "My best student. Emma, this is... a customer."

"Emma," I said, extending my hand. "I'm writing about Victorian erotic literature."

She raised an eyebrow. "Really? How... academic of you."

"It's more interesting than it sounds," I defended.

"Everything is more interesting than it sounds," she replied, and there was something in her tone that made me want to know everything about her.

The three of us ended up in the back room, surrounded by leather-bound books and the smell of aging paper. Alistair had brought out a rare copy of "The Pearl," and Emma was examining it with the kind of reverence usually reserved for religious texts.

"You know," she said, tracing the embossed cover, "they say the Victorians were repressed. But really, they were just better at hiding things."

"Better at writing about them too," I added.

She looked at me then, really looked at me, and I felt like she was seeing right through me. "You write about them, but have you ever... lived them?"

"Lived what?" I asked, though I knew exactly what she meant.

"The passion. The secrecy. The thrill of doing something you're not supposed to do."

Alistair cleared his throat. "Emma, perhaps we should focus on the research."

"Perhaps we should focus on the experience," she countered, and the air in the room changed, thickened with possibility.

That's how it started. The three of us, meeting in the back of the bookshop, talking about Victorian passions while living out our own. Emma was doing her PhD on transgressive literature, Alistair was her supervisor, and I was... well, I was the wildcard, the American who didn't understand the rules.

The first time it happened was accidental, sort of. We were working late, cataloging a new acquisition, and Emma was telling us about a particularly scandalous passage from an anonymous Victorian novel.

"The thing about these stories," she said, her voice low and intimate, "is that they're not just about sex. They're about power. About who gets to say yes, who gets to say no, who gets to want."

She was standing between us, close enough that I could smell her perfume—something floral and expensive. Alistair was watching her with an expression that was part academic interest, part something else entirely.

"Show us," I heard myself say. "Show us what you mean."

Emma looked at Alistair, and he nodded slightly. "Perhaps a practical demonstration would be... educational."

She kissed me first, her mouth soft and confident, her hands tangling in my hair. I was surprised, but not that surprised. I'd been waiting for this moment since I'd first seen her.

Then Alistair was behind her, his hands on her waist, his mouth on her neck, and the three of us were tangled together in a way that felt both inevitable and completely insane.

We ended up on the floor of the back room, surrounded by books that had seen centuries of secrets. The air was thick with the smell of old paper and new desire, and I felt like I was living in one of the stories I was supposed to be writing about.

Emma was like a conductor, directing both of us, telling us what to do, how to touch, where to kiss. Alistair was surprisingly submissive, letting her lead, letting her control the pace. I was just along for the ride, happy to be part of whatever this was.

"Like this," she'd whisper, positioning my hands, adjusting Alistair's mouth. "No, slower. Yes, right there."

It was the most educational experience of my academic career.

After that, it became a regular thing. We'd meet in the bookshop after closing, lock the door, and explore the kind of Victorian passions we were supposed to be studying. Sometimes it was just Emma and me, sometimes just Emma and Alistair, sometimes all three of us together.

The sex was incredible, but it was more than that. It was research, or at least that's what we told ourselves. We were living the literature, experiencing the transgression, understanding the psychology behind the prose.

"I think I understand now," I said one night, lying tangled together on Alistair's Persian rug. "Why they wrote these stories. Why they took such risks."

"Because desire doesn't care about rules," Emma said, her head on my chest. "It doesn't care about society or propriety or what's 'proper.' It just wants."

"And because secrets are hot," Alistair added, and we all laughed.

But it wasn't just about the sex. It was about the connection, the three of us finding something in each other that we hadn't found anywhere else. Emma was brilliant and challenging, pushing us intellectually as well as physically. Alistair was experienced and patient, teaching us things we didn't know we needed to learn. And I... I was the catalyst, the one who didn't know the rules, the one who wasn't afraid to break them.

The problem with secrets, though, is that they have a way of coming out.

It started with gossip. Someone saw us leaving the bookshop together too late, too often. Someone noticed the way Emma looked at me in class, the way Alistair's eyes would follow her across a room.

Then came the questions from the department head. "Unusual closeness," "inappropriate relationships," "concerns about professional boundaries."

Emma tried to play it off. "We're just working on a research project together."

Alistair tried to be professional. "It's purely academic."

But the truth was, it wasn't purely anything anymore. It was personal, it was complicated, it was real.

The breaking point came when Emma's husband found out. She was married—something she'd never mentioned, something Alistair knew but I didn't. He showed up at the bookshop one afternoon, looking angry and hurt.

"Emma," he said, his voice tight with pain. "I know."

She didn't even try to deny it. "I'm sorry."

"Sorry doesn't fix this," he said, and then he looked at Alistair, then at me. "All of you? Really?"

That's when everything fell apart.

Emma took a leave of absence from the university. Alistair was put on administrative leave pending an investigation. I was told to find a new dissertation advisor, to find a new research topic, to find a new life.

The bookshop stayed open, but it felt different without them. The secrets were gone, replaced by the hollow echo of what we'd lost.

I finished my dissertation, but it wasn't the same. The words felt hollow, the analysis felt clinical. I'd experienced the passion, the secrecy, the thrill, and now I was supposed to write about it like it was just words on a page.

Six months later, I got a package in the mail. No return address, just my name and the university address. Inside was a rare copy of "The Pearl," and a note in Emma's handwriting.

"Some stories are meant to be lived, not just read. Thank you for helping me live mine."

I keep the book on my shelf, a reminder of that semester in Edinburgh, of the three of us in the back room of The Last Page, living Victorian secrets in modern times.

Sometimes I wonder what happened to them. Sometimes I wonder if they found each other again, if they're still living the stories they used to only study.

Mostly, though, I just remember. The smell of old paper and rain and secrets. The feeling of Emma's mouth on mine, Alistair's hands on my skin. The thrill of doing something we weren't supposed to do.

Some stories aren't meant to have happy endings. Some stories are just meant to be lived, then remembered.

And that's enough.

That has to be enough.`
};
